import { Component, signal, ViewEncapsulation, computed } from '@angular/core';
import { ActionSheetController, AlertButton, AlertController, AlertInput, RefresherCustomEvent } from '@ionic/angular';
import { filter, map, Observable, Subscription, take } from 'rxjs';
import { PushManagerProvider } from 'src/providers/push/push-manager.provider';
import { PushProvider } from 'src/providers/push/push.provider';
import { ChatProvider } from 'src/providers/chat.provider';
import { FieldProvider } from 'src/providers/field.provider';
import { LoadingProvider } from 'src/providers/loading.provider';
import { RequestApplicationSorting, RequestProvider, RequestStatusDictionary } from 'src/providers/request.provider';
import { TranslatorProvider } from 'src/providers/translator.provider';
import { ChatItemStyle, ChatItemType } from 'src/shared/models/chat/chat-item.model';
import { ChatType } from 'src/shared/models/chat/chat.model';
import { FieldType } from 'src/shared/models/field/field';
import { FieldGroupModel, FieldGroupPlacement } from 'src/shared/models/field/field-group';
import { UserFieldResponse } from 'src/shared/models/field/user-field-response';
import { NotificationType } from 'src/shared/models/push-notification/push-notification';
import { CreateRequestApplicationModel } from 'src/shared/models/requests/request-application-create.model';
import { RequestApplicationModel } from 'src/shared/models/requests/request-application.model';
import { dateRangeDelimiter, defaultDateTimeRangeFormat } from 'src/constants/defaults';
import { RequestStatus, RequestType } from 'src/shared/models/requests/request.model';
import { ScreenAnimationProvider } from 'src/providers/screen-animation-provider';
import { HapticsProvider, HapticType } from 'src/providers/haptics.provider';
import { TabsProvider } from 'src/providers/tabs/tabs.provider';
import { SyncService } from 'src/providers/sync.service';
import { Network } from '@capacitor/network';
import * as fp from 'lodash/fp';
import moment from 'moment';

type SelectedRequestPage = 'init' | 'requests-list' | 'requests-create';

@Component({
  selector: 'rota-request-list-page',
  styleUrl: 'requests.page.scss',
  templateUrl: 'requests.page.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class RequestListPage {

  pushSub: Subscription;

  sortBy = signal<RequestApplicationSorting>(RequestApplicationSorting.None);

  isChatOpen = signal<boolean>(false);

  isLoading = signal<boolean>(false);

  selectedPage = signal<SelectedRequestPage>('init');

  public alertButtons: AlertButton[] = [
    { text: this.translator.dictionary.General_Cancel, role: 'destructive' },
    { text: this.translator.dictionary.General_Okay, role: 'ok' },
  ];

  get showSortLabel() {
    return this.sortBy() !== RequestApplicationSorting.None;
  }

  get showLoadButton() {
    return this.requestsService.total > this.requestsService.count;
  }

  get applications$(): Observable<RequestApplicationModel[]> {
    if (this.sortBy() === RequestApplicationSorting.None) {
      return this.requestsService.applications$;
    }
    else {
      return this.requestsService.sortedApplications$;
    }
  }

  get fieldGroups$() {
    return this.fieldProvider.fieldGroups$
      .pipe(
        map((fg: any) => fg && fg.filter((f: any) => f.placement === FieldGroupPlacement.CreateNotificationRequestPage))
      );
  }

  get filterBy(): typeof RequestApplicationSorting {
    return RequestApplicationSorting;
  }

  trackByAppId = (_: number, a: RequestApplicationModel) => a.id;

  // Computed signal to get the current list of applications
  private applicationsSignal = computed(() => {
    if (this.sortBy() === RequestApplicationSorting.None) {
      return this.requestsService.applications();
    } else {
      return this.requestsService.sortedApplications();
    }
  });

  // Method to get isRead through signal (used in template)
  // Angular will track changes through computed signal applicationsSignal
  isApplicationRead(applicationId: string): boolean {
    const apps = this.applicationsSignal();
    const app = apps.find(a => a.id === applicationId);
    return app?.isRead ?? false;
  }

  constructor(
    private translator: TranslatorProvider,
    private requestsService: RequestProvider,
    private chatService: ChatProvider,
    private pushProvider: PushProvider,
    private fieldProvider: FieldProvider,
    private alertController: AlertController,
    private pushManagerProvider: PushManagerProvider,
    private loading: LoadingProvider,
    private syncService: SyncService,
    private tabsProvider: TabsProvider,
    private hapticsProvider: HapticsProvider,
    private actionSheetController: ActionSheetController,
    private screenAnimationProvider: ScreenAnimationProvider,
  ) { }

  async ionViewWillEnter() {
    this.isLoading.set(true);

    this.selectedPage.set('requests-list');

    this.subscribeToNewMessage();

    await this.requestsService.fetchApplications();

    this.isLoading.set(false);
  }

  async handleRefresh(event: RefresherCustomEvent) {
    this.isLoading.set(true);
    await this.requestsService.fetchApplications();
    this.isLoading.set(false);
    event.target.complete();
  }

  getSortName() {
    switch (this.sortBy()) {
      case RequestApplicationSorting.Unread: return this.translator.dictionary.Request_unread
      case RequestApplicationSorting.Approved: return this.translator.dictionary.Request_approved
      case RequestApplicationSorting.Rejected: return this.translator.dictionary.Request_rejected
      case RequestApplicationSorting.Requested: return this.translator.dictionary.Request_requested
      case RequestApplicationSorting.Feedback: default: return this.translator.dictionary.Request_feedback
    }
  }

  subscribeToNewMessage() {
    this.pushSub = this.pushProvider.onPushNotification$
      .pipe(filter(notification => notification.type === NotificationType.RequestMessage))
      .subscribe(async notify => {
        const notifyMeta = notify.additional ? JSON.parse(notify.additional) : null;

        const application = this.requestsService.findApplication(notify.associatedEntryId);
        if (!application) return;

        if (notifyMeta) {
          this.chatService.appendNewMessage({
            title: notifyMeta.RequestStatus === RequestStatus.Approved
              ? application.title + ' ' + this.translator.dictionary.was_approved
              : notifyMeta.RequestStatus === RequestStatus.Rejected
                ? application.title + ' ' + this.translator.dictionary.was_rejected
                : '',
            username: notify.createdBy,
            message: notifyMeta.RequestMessage,
            fromClient: notifyMeta.FromClient,
            createdAt: notify.createdAt,
            icon: RequestStatusDictionary[notifyMeta.RequestStatus as RequestStatus],
            itemType: notifyMeta.RequestStatus === RequestStatus.Approved || notifyMeta.RequestStatus === RequestStatus.Rejected
              ? ChatItemType.Notice
              : ChatItemType.Message,
            style: notifyMeta.RequestStatus === RequestStatus.Approved
              ? ChatItemStyle.Success
              : notifyMeta.RequestStatus === RequestStatus.Rejected
                ? ChatItemStyle.Danger
                : ChatItemStyle.Warning,
          });
        }

        if (this.isChatOpen()) {
          await this.requestsService.read(notify.associatedEntryId);
          await this.pushManagerProvider.fetchNotifications();
        }
        else {
          this.requestsService.updateApplicationIsRead(notify.associatedEntryId, false);
        }
      });
  }

  async openChat(application: RequestApplicationModel) {
    if (!application.isRead) {
      this.requestsService.read(application.id)
        .then(() => this.pushManagerProvider.fetchNotifications());
    }

    this.isChatOpen.set(true);

    await this.chatService.open(RequestApplicationModel.toChatModel(application, this.translator), ChatType.Requests, () => {
      this.isChatOpen.set(false);
      this.requestsService.updateApplication(application.id);
    });
  }

  async loadMoreApplications() {
    this.isLoading.set(true);
    const page = this.requestsService.filter.page + 1;
    await this.requestsService.uploadApplications(page);

    if (this.sortBy() !== RequestApplicationSorting.None) {
      this.filterSelected(this.sortBy());
    }

    this.isLoading.set(false);
  }

  getPreviewMessage(application: RequestApplicationModel): string {
    const lastRequest = application.requests[application.requests.length - 1];
    const previewMessage = RequestApplicationModel.getMessagePreview(application.title, lastRequest!, this.translator);

    return previewMessage;
  }

  getStatusIcon(application: RequestApplicationModel): string {
    if (!application.requests.length) return '';

    const lastStatus = application.requests
      .filter(f => !f.fromClient || f.type === RequestType.Request || f.type === RequestType.UpdateRequest)
      .map(m => m.status)
      .pop();

    return RequestStatusDictionary[lastStatus!];
  }

  getDate(application: RequestApplicationModel): string {
    if (!application.requests.length) return '';

    return application.requests[application.requests.length - 1].createdAt.toString();
  }

  getType(application: RequestApplicationModel): string {
    if (!application.requests.length) return '';

    const lastStatus = application.requests
      .filter(f => !f.fromClient || f.type === RequestType.Request || f.type === RequestType.UpdateRequest)
      .map(m => m.status)
      .pop();

    return `request-card ${RequestStatus[lastStatus!].toLowerCase()}`;
  }

  filterSelected(sortBy: RequestApplicationSorting) {
    this.sortBy.set(sortBy);
    this.requestsService.sortApplications(this.sortBy());
    this.hapticsProvider.vibrate(HapticType.Click);
  }

  async showFilters() {
    const sheetFilterBy = await this.actionSheetController.create({
      header: 'Filter',
      buttons: [
        { text: this.translator.dictionary.Request_filter_all, role: this.sortBy() === RequestApplicationSorting.None ? 'selected' : '', data: RequestApplicationSorting.None },
        { text: this.translator.dictionary.Request_unread, role: this.sortBy() === RequestApplicationSorting.Unread ? 'selected' : '', data: RequestApplicationSorting.Unread },
        { text: this.translator.dictionary.Request_approved, role: this.sortBy() === RequestApplicationSorting.Approved ? 'selected' : '', data: RequestApplicationSorting.Approved },
        { text: this.translator.dictionary.Request_rejected, role: this.sortBy() === RequestApplicationSorting.Rejected ? 'selected' : '', data: RequestApplicationSorting.Rejected },
        { text: this.translator.dictionary.Request_requested, role: this.sortBy() === RequestApplicationSorting.Requested ? 'selected' : '', data: RequestApplicationSorting.Requested },
        { text: this.translator.dictionary.Request_feedback, role: this.sortBy() === RequestApplicationSorting.Feedback ? 'selected' : '', data: RequestApplicationSorting.Feedback },

        { text: this.translator.dictionary.General_Cancel, role: "cancel" },
      ],
      translucent: true,
    });

    sheetFilterBy.onWillDismiss().then(async data => {
      if (data.role === 'cancel' || data.role === 'backdrop') return;

      this.hapticsProvider.vibrate(HapticType.Click);

      this.filterSelected(data.data);
    });

    await sheetFilterBy.present();
  }

  async goBack() {
    await this.tabsProvider.forwardBack();

    this.sortBy.set(RequestApplicationSorting.None);
  }

  segmentChanged(value: SelectedRequestPage) {
    this.hapticsProvider.vibrate(HapticType.Click);
    this.selectedPage.set(value);
  }

  [key: string]: any;
  handleSubmit(values: Array<UserFieldResponse>) {
    if (this[`${this.handleSubmit.name}Pending`]) return;

    this[`${this.handleSubmit.name}Pending`] = true;

    this.fieldProvider.fieldGroups$
      .pipe(
        filter(fg => fg && fg.length > 0),
        map(fg => fg && fg.filter(f => f.placement === FieldGroupPlacement.CreateNotificationRequestPage)),
        take(1)
      )
      .subscribe(async (groups: FieldGroupModel[]) => {
        const radioField = values.find(v => v.meta!.field!.type === FieldType.Radio);
        const radioOptions = fp.getOr([], "meta.field.options", radioField);
        const selectedOption = radioOptions.find((o: any) => o.id === radioField!.response);
        const selectedGroup = groups.find((g: any) => g.optionId === selectedOption.id)!;
        const title = selectedOption ? selectedOption.name : "Radio Buttons were not provided. Title can't be set.";
        const model = new CreateRequestApplicationModel(
          title,
          fp.pull(radioField, values) as UserFieldResponse[],
          selectedGroup.id,
        );

        if (this.isDateSelected(values)) {
          await this.showConfirmationModal(model);
        }
        else {
          await this.showMissingDateAlert(title);
        }

        delete this[`${this.handleSubmit.name}Pending`];
      });
  }

  private isDateSelected(values: Array<UserFieldResponse>): boolean {
    return values.some(v =>
      (v.meta?.field?.type === FieldType.DateRange ||
        v.meta?.field?.type === FieldType.DateTime ||
        v.meta?.field?.type === FieldType.DateTimeRange) &&
      v.response && v.response.trim() !== ''
    );
  }

  private async showMissingDateAlert(title: string): Promise<void> {
    this.screenAnimationProvider.shake();

    setTimeout(async () => {
      const alert = await this.alertController.create({
        header: title,
        message: this.translator.dictionary.Select_date,
        buttons: [this.translator.dictionary.General_Okay],
      });

      await alert.present();
    }, 300);
  }

  async showConfirmationModal(model: CreateRequestApplicationModel) {
    const hasInternetConnection = (await Network.getStatus()).connected;
    if (!hasInternetConnection) {
      await this.showAlertNoInternetConnection();
      return;
    }

    const alertInputs: AlertInput[] = [];

    model.userFieldResponses.forEach(f => {
      let value: string;

      const fieldType = f.meta?.field?.type;
      const format = f.meta?.field?.displayFormat;

      if (fieldType === FieldType.DateTime) {
        value = this.formatAttachmantDates(f.response, format);

        alertInputs.push({ type: 'text', disabled: true, value, });
      }
      else if (fieldType === FieldType.DateRange && typeof f.response === 'string') {
        const [from, to] = f.response.split(dateRangeDelimiter);
        const formattedFrom = this.formatAttachmantDates(from, format);
        const formattedTo = this.formatAttachmantDates(to, format);
        value = `${formattedFrom} - ${formattedTo}`;

        alertInputs.push({ type: 'text', disabled: true, value: formattedFrom, });
        alertInputs.push({ type: 'text', disabled: true, value: formattedTo, });
      }
      else if (fieldType === FieldType.DateTimeRange && typeof f.response === 'string') {
        const [from, to] = f.response.split(dateRangeDelimiter);
        const formattedFrom = this.formatAttachmantDateTimes(from, defaultDateTimeRangeFormat, format);
        const formattedTo = this.formatAttachmantDateTimes(to, defaultDateTimeRangeFormat, format);
        value = `${formattedFrom} - ${formattedTo}`;

        alertInputs.push({ type: 'text', disabled: true, value: formattedFrom, });
        alertInputs.push({ type: 'text', disabled: true, value: formattedTo, });
      }
      else {
        value = f.response;

        alertInputs.push({ type: 'text', disabled: true, value, });
      }
    });

    const alert = await this.alertController.create({
      header: this.translator.dictionary.Confirmation,
      message: model.title,
      inputs: alertInputs,
      buttons: this.alertButtons,
      cssClass: 'custom-alert',
    });

    alert.onWillDismiss().then(async data => {
      if (data.role === 'ok') {
        const hasInternetConnection = (await Network.getStatus()).connected;
        if (!hasInternetConnection) {
          await this.showAlertNoInternetConnection();
          return;
        }

        const loading = await this.loading.create();
        await loading.present();

        try {
          const format = model.userFieldResponses[0]?.meta?.field?.displayFormat;
          const wrapRes = await this.syncService.wrapRequest(SyncService.RETRY_PENDING_REQUESTS, model.toDto(format));
          const application = await this.requestsService.getApplication(fp.get("id", wrapRes.result));

          const alert = await this.alertController.create({
            header: this.translator.dictionary.Request_Title,
            message: wrapRes.message,
            buttons: [{
              text: this.translator.dictionary.General_Okay,
              handler: async () => {
                if (application) {
                  await this.openCreatedRequest(application);
                  this.selectedPage.set('requests-list');
                }
              }
            }]
          });

          await alert.present();
        }
        catch (err) {
          console.error(err);
        }
        finally {
          await loading.dismiss();
        }
      }
    });

    await alert.present();
  }

  private formatAttachmantDates(response: any, format?: string) {
    const newDate = this.toAbsoluteDateTime(response);
    return moment(newDate, format).format(format);
  }

  private formatAttachmantDateTimes(response: any, oldFormat: string, newFormat?: string) {
    const newDate = this.toAbsoluteDateTime(response);
    return moment(newDate, oldFormat).format(newFormat);
  }

  toAbsoluteDateTime(dateTime: string) {
    if (dateTime.includes("Z"))
      dateTime = dateTime.substring(0, dateTime.length - (dateTime.length - dateTime.indexOf("Z")));
    if (dateTime.includes("+"))
      dateTime = dateTime.substring(0, dateTime.length - (dateTime.length - dateTime.indexOf("+")));
    return dateTime;
  }

  async showAlertNoInternetConnection() {
    const alert = await this.alertController.create({
      header: this.translator.dictionary.No_Connection_try_later_Header,
      message: this.translator.dictionary.No_internet_connection,
      buttons: [{ text: this.translator.dictionary.General_Okay }],
    });

    await alert.present();
  }

  async openCreatedRequest(application: RequestApplicationModel) {
    this.requestsService.read(application.id);
    await this.chatService.open(RequestApplicationModel.toChatModel(application, this.translator), ChatType.Requests);
  }

  ionViewWillLeave(): void {
    if (this.pushSub) this.pushSub.unsubscribe();
    this.isChatOpen.set(false);
  }
}
